import { Markup } from './BracketPriceState';
import {
  calculateCoverageFromSalesFactor,
  calculateNewSalesFactor,
  calculateSalesCoverageFromCostAndSalesPrice,
  calculateSalesFactorFromDG,
  roundToMaxDecimalCount,
  STANDARD_VAT_FACTOR,
} from '@retail/utils';

export const calculateMarkupFromSalesPrice = (
  newSalesPrice: number,
  includeVat: boolean,
  currentCostPrice?: number,
  gtin?: string,
  fPakSalesPrice?: number
): Markup => {
  if (!currentCostPrice) {
    throw new Error('Cannot set sales price without having a cost price');
  }

  if (!gtin) {
    throw new Error('Cannot set sales price without having a package gtin');
  }

  const newSalesCoverage = calculateSalesCoverageFromCostAndSalesPrice(
    currentCostPrice,
    newSalesPrice
  );
  const newSalesFactor = calculateNewSalesFactor(currentCostPrice, newSalesPrice);

  const discount = fPakSalesPrice
    ? ((fPakSalesPrice - newSalesPrice) / fPakSalesPrice) * 100
    : undefined;

  return {
    salesFactor: roundToMaxDecimalCount(newSalesFactor, 6),
    salesCoverage: roundToMaxDecimalCount(newSalesCoverage, 6),
    salesPrice: includeVat ? newSalesPrice / STANDARD_VAT_FACTOR : newSalesPrice,
    salesPriceIncVat: includeVat ? newSalesPrice : newSalesPrice * STANDARD_VAT_FACTOR,
    gtin,
    discount,
  };
};

export const calculateMarkupFromCoverage = (
  newSalesCoverage: number,
  currentCostPriceIncVat?: number,
  currentCostPriceExVat?: number,
  fPakPriceIncVat?: number,
  gtin?: string
): Markup => {
  if (!currentCostPriceExVat || !currentCostPriceIncVat) {
    throw new Error('Cannot set discount without having a cost price');
  }

  if (!gtin) {
    throw new Error('Cannot set sales price without having a package gtin');
  }

  const newSalesFactor = calculateSalesFactorFromDG(newSalesCoverage);

  const newSalesPriceWithoutVat = currentCostPriceExVat && currentCostPriceExVat * newSalesFactor;
  const newSalesPriceIncVat = currentCostPriceIncVat && currentCostPriceIncVat * newSalesFactor;

  const discount =
    fPakPriceIncVat && newSalesPriceIncVat
      ? ((fPakPriceIncVat - newSalesPriceIncVat) / fPakPriceIncVat) * 100
      : undefined;

  return {
    salesFactor: roundToMaxDecimalCount(newSalesFactor, 6),
    salesCoverage: roundToMaxDecimalCount(newSalesCoverage, 6),
    salesPrice: newSalesPriceWithoutVat,
    salesPriceIncVat: newSalesPriceIncVat,
    discount,
    gtin,
  };
};

export const calculateMarkupFromDiscount = (
  newDiscount: number,
  currentFPakSalesPriceIncVat?: number,
  currentFPakSalesPriceExVat?: number,
  currentFPakCostPriceExVat?: number,
  gtin?: string
): Markup => {
  if (!currentFPakCostPriceExVat) {
    throw new Error('Cannot set discount without having a cost price');
  }

  if (!currentFPakSalesPriceIncVat || !currentFPakSalesPriceExVat) {
    throw new Error('Cannot set discount without having a F-PAK price');
  }

  if (!gtin) {
    throw new Error('Cannot set sales price without having a package gtin');
  }

  const newSalesPriceExVat = currentFPakSalesPriceExVat * (1 - newDiscount / 100);
  const newSalesPriceIncVat = currentFPakSalesPriceIncVat * (1 - newDiscount / 100);

  const salesFactor = newSalesPriceExVat / currentFPakCostPriceExVat;

  return {
    salesPrice: newSalesPriceExVat,
    salesPriceIncVat: newSalesPriceIncVat,
    salesFactor: roundToMaxDecimalCount(salesFactor, 6),
    salesCoverage: roundToMaxDecimalCount(calculateCoverageFromSalesFactor(salesFactor), 6),
    discount: newDiscount,
    gtin,
  };
};
